import * as React from "react";
import type { API } from "@/api/client";
import { api as defaultApi } from "@/api/instance";
import type { CopilotSummary } from "../types";
import { useCopilot } from "./useCopilot";
import { useAudioRecordings } from "./useAudioRecordings";
import type { VisitCardUpdatePayload } from "./useVisitCard";

export type UseMeetingSessionArgs = {
  api?: API<unknown>;
  selectedProfileId: string;
  selectedProfileTitle: string;
  telephone: string | undefined;
  /** Профиль готов (status=success и есть profileData) — presentator.json уже на диске. */
  profileReady?: boolean;
  visitCard: {
    init: (
      sessionId: string,
      phone?: string,
      profileName?: string,
    ) => Promise<void>;
    reset: () => void;
    onVisitCardUpdate: (data: VisitCardUpdatePayload) => void;
  };
  featureFlags: {
    copilot: boolean;
    visit_card: boolean;
  };
};

export type ProfileSwitchOpts = {
  /** Recording / copilot / demo still active — do not clear. */
  isLive: boolean;
  /** Post-stop leftover panel (keepPanelAfterStop / demo keepPanel). */
  hasLeftover: boolean;
};

/**
 * Owns the circular dependency between useCopilot and useAudioRecordings
 * (attachCopilotSummary / markSummaryFailed refs) plus visit-card init
 * that depends on copilotSessionId.
 */
export function useMeetingSession({
  api = defaultApi,
  selectedProfileId,
  selectedProfileTitle,
  telephone,
  profileReady = false,
  visitCard,
  featureFlags,
}: UseMeetingSessionArgs) {
  const [isRecording, setIsRecording] = React.useState(false);
  const [meetingProfileId, setMeetingProfileId] = React.useState<string | null>(
    null,
  );
  const meetingProfileIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    meetingProfileIdRef.current = meetingProfileId;
  }, [meetingProfileId]);

  /** Set when the user leaves via sidebar/create — rename must not look like a leave. */
  const userSwitchRef = React.useRef(false);
  const selectedProfileIdRef = React.useRef(selectedProfileId);
  React.useEffect(() => {
    selectedProfileIdRef.current = selectedProfileId;
  }, [selectedProfileId]);

  const attachCopilotSummaryRef = React.useRef<
    (sessionId: string, summary: CopilotSummary) => void
  >(() => {});
  const markSummaryFailedRef = React.useRef<(sessionId: string) => void>(() => {});

  const {
    isActive: isCopilotActive,
    recommendations,
    checkpoints,
    showMissedCheckpoints,
    keepPanelAfterStop,
    error: copilotError,
    copilotSessionId,
    copilotSessionIdRef,
    startCopilot,
    stopCopilot,
    watchSessionDone,
    clearRecommendations: clearRecommendationsInner,
    appendRecommendations,
    applyCheckpoints,
    resetCheckpointsForDemo,
    markMissedCheckpoints,
    markLiveRecordingStopped,
  } = useCopilot(api, {
    isRecording,
    // Keep isFinalizing true until summary arrives or retries fail — do not
    // clear it on onSessionDone (SSE done fires before summary is ready).
    onSummary: (sessionId, summary) =>
      attachCopilotSummaryRef.current(sessionId, summary),
    onSummaryFailed: (sessionId) => markSummaryFailedRef.current(sessionId),
    onSseEvent: (data) => {
      if (data.type === "visit_card_update") {
        visitCard.onVisitCardUpdate(data as unknown as VisitCardUpdatePayload);
      }
    },
  });

  const beginMeeting = React.useCallback((profileId: string) => {
    if (!profileId) return;
    // Starting a meeting on another profile: drop the previous card so its
    // FIO does not flash before the new init returns.
    if (
      meetingProfileIdRef.current != null &&
      meetingProfileIdRef.current !== profileId
    ) {
      visitCard.reset();
    }
    setMeetingProfileId(profileId);
    meetingProfileIdRef.current = profileId;
  }, [visitCard.reset]);

  const clearRecommendations = React.useCallback(() => {
    clearRecommendationsInner();
    // Drop ownership only when the meeting is already over (leftover Clear).
    // During live recording, keep meetingProfileId so panels stay scoped.
    if (!isRecording && !isCopilotActive) {
      visitCard.reset();
      setMeetingProfileId(null);
      meetingProfileIdRef.current = null;
    }
  }, [clearRecommendationsInner, isCopilotActive, isRecording, visitCard.reset]);

  /**
   * Sidebar / create leave. Rename (adoptRenamedProfile) must NOT call this.
   * Clears leftover only when leaving the meeting profile after stop.
   */
  const handleProfileSwitch = React.useCallback(
    (nextId: string, opts: ProfileSwitchOpts) => {
      userSwitchRef.current = true;
      const meetingId = meetingProfileIdRef.current;
      if (!meetingId) return;
      if (opts.isLive) return;
      const fromId = selectedProfileIdRef.current;
      if (
        opts.hasLeftover &&
        fromId === meetingId &&
        nextId !== meetingId
      ) {
        clearRecommendationsInner();
        visitCard.reset();
        setMeetingProfileId(null);
        meetingProfileIdRef.current = null;
      }
    },
    [clearRecommendationsInner, visitCard.reset],
  );

  // Rename of the meeting profile (tour stamp→FIO): follow the new key.
  // User switches set userSwitchRef and must not rewrite meetingProfileId.
  const prevSelectedForRenameRef = React.useRef(selectedProfileId);
  React.useEffect(() => {
    const prev = prevSelectedForRenameRef.current;
    prevSelectedForRenameRef.current = selectedProfileId;

    if (userSwitchRef.current) {
      userSwitchRef.current = false;
      return;
    }

    const meetingId = meetingProfileIdRef.current;
    if (!meetingId) return;
    if (prev === meetingId && selectedProfileId && selectedProfileId !== meetingId) {
      setMeetingProfileId(selectedProfileId);
      meetingProfileIdRef.current = selectedProfileId;
    }
  }, [selectedProfileId]);

  // Автоматически инициируем карточку при старте копилот-сессии.
  // Phone берём из метаданных профиля — он нужен бэку чтобы определить
  // существующего клиента в Axapta и подтянуть pre-fill (ФИО, email,
  // последнее обращение). Если в момент старта phone ещё не подгрузился —
  // делаем повторный init когда он появится (init на бэке идемпотентен:
  // существующую карточку он не перетирает, manual-правки сохраняет).
  // Третий триггер — профиль стал готов (presentator.json появился уже
  // после первого init, который ушёл во время processing анкеты).
  // profile_name — ключ папки встречи (meetingProfileId), не чужой профиль
  // из сайдбара. Rename той же встречи (штамп→ФИО) — re-init с новым ключом.
  // User switch на другой профиль — не трогаем визитку.
  const lastVisitCardInit = React.useRef<{
    sid: string | null;
    phoneAttached: boolean;
    profileReady: boolean;
    profileId: string | null;
  }>({
    sid: null,
    phoneAttached: false,
    profileReady: false,
    profileId: null,
  });
  React.useEffect(() => {
    if (!copilotSessionId || !featureFlags.copilot || !featureFlags.visit_card)
      return;

    const visitProfileId = meetingProfileId ?? selectedProfileId;
    // Away from the meeting profile: ignore selectedMeta-driven triggers.
    if (meetingProfileId && selectedProfileId !== meetingProfileId) return;

    const phone = telephone;
    const last = lastVisitCardInit.current;
    const isNewSession = last.sid !== copilotSessionId;
    const phoneJustArrived = !last.phoneAttached && Boolean(phone);
    const profileJustBecameReady = !last.profileReady && profileReady;
    const profileIdChanged =
      Boolean(visitProfileId) && last.profileId !== visitProfileId;
    if (
      !isNewSession &&
      !phoneJustArrived &&
      !profileJustBecameReady &&
      !profileIdChanged
    )
      return;
    lastVisitCardInit.current = {
      sid: copilotSessionId,
      phoneAttached: Boolean(phone),
      profileReady,
      profileId: visitProfileId || null,
    };
    void visitCard.init(
      copilotSessionId,
      phone,
      visitProfileId || selectedProfileTitle || undefined,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    copilotSessionId,
    featureFlags.copilot,
    featureFlags.visit_card,
    telephone,
    selectedProfileId,
    selectedProfileTitle,
    profileReady,
    meetingProfileId,
  ]);

  const {
    localAudioByProfileId,
    renameLocalAudioRecord,
    deleteLocalAudioRecord,
    onRecordingStart,
    onChunkReady,
    onRecorded,
    onRetryUploadLocalRecord,
    setFinalizingRecord,
    attachCopilotSummary,
    markSummaryFailed,
    addLocalDemoRecord: addLocalDemoRecordInner,
  } = useAudioRecordings(api, selectedProfileId, copilotSessionIdRef);

  const addLocalDemoRecord = React.useCallback(
    ({ durationSec }: { durationSec: number }): string => {
      const profileId = meetingProfileIdRef.current ?? selectedProfileId;
      return addLocalDemoRecordInner({ durationSec, profileId });
    },
    [addLocalDemoRecordInner, selectedProfileId],
  );

  React.useEffect(() => {
    attachCopilotSummaryRef.current = attachCopilotSummary;
    markSummaryFailedRef.current = markSummaryFailed;
  }, [attachCopilotSummary, markSummaryFailed]);

  return {
    isRecording,
    setIsRecording,
    meetingProfileId,
    beginMeeting,
    handleProfileSwitch,
    isCopilotActive,
    recommendations,
    checkpoints,
    showMissedCheckpoints,
    keepPanelAfterStop,
    copilotError,
    copilotSessionId,
    copilotSessionIdRef,
    startCopilot,
    stopCopilot,
    watchSessionDone,
    clearRecommendations,
    appendRecommendations,
    applyCheckpoints,
    resetCheckpointsForDemo,
    markMissedCheckpoints,
    markLiveRecordingStopped,
    localAudioByProfileId,
    renameLocalAudioRecord,
    deleteLocalAudioRecord,
    onRecordingStart,
    onChunkReady,
    onRecorded,
    onRetryUploadLocalRecord,
    setFinalizingRecord,
    addLocalDemoRecord,
  };
}
